"use client";
import React, { useEffect, useMemo, useState } from "react";

import {
  PaymentElement,
  StripeWrapper,
  useStripe,
  useElements,
  addCardApi,
  createStripeSetupIntent,
  DEFAULT_ELEMENT_STYLE_OPTIONS,
  DEFAULT_PAYMENT_ELEMENT_OPTIONS,
} from "@stadium/services/stripe";
import { PAYMENT_ERROR_CONFIG } from "@stadium/services/react-toastify";

import { jsonErrorFormatter } from "@stadium/helpers/formatter";
import ButtonWithLoader from "@stadium/ui/src/components/button/button-with-loader";
import Loaders from "@stadium/ui/src/components/loaders/loaders";
import TextInput from "@stadium/ui/src/components/text-input/text-input";
import styles from "./main.module.scss";

const emptyObj = {};
const emptyArr = [];
const emptyFn = () => null;
const cardNameFieldId = "card-name";
const GENERIC_ERROR_MESSAGE = "There was a problem saving your card details.";

const AddCardForm = (props) => {
  const {
    id = "add-card-form",
    submitBtn,
    classes,
    paymentElementOptions = DEFAULT_PAYMENT_ELEMENT_OPTIONS,
    onPaymentError = emptyFn,
    onSuccess = emptyFn,
    apiClient,
    showError,
    clearErrors,
    showCardNote = true,
    extraInfo = "",
    renderCustomFooter = null,
    addCardFormClasses = "",
  } = props;
  const {
    bodyWrapper = "",
    input = "",
    inputLabel = "",
    actionButtonsWrapper = "",
  } = classes || emptyObj;

  const [isLoading, setLoading] = useState(false);
  const [isCardFormLoading, setIsCardFormLoading] = useState(true);
  const [cardName, setCardName] = useState("");
  const [isCardFormComplete, setIsCardFormComplete] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  useEffect(clearErrors, emptyArr);

  const handleSubmit = async (event) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    setLoading(true);

    let confirmSetupData = null;

    try {
      confirmSetupData = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });
      const { error } = confirmSetupData;

      if (error) {
        setLoading(false);
        onPaymentError(error);
      }
    } catch (er) {
      showError(
        "Something went wrong, Please contact support for further assistance.",
        "",
        PAYMENT_ERROR_CONFIG,
      );
      setLoading(false);
      throw er;
    }

    // eslint-disable-next-line consistent-return
    if (!confirmSetupData.error)
      return addCardApi(apiClient, {
        credit_card: {
          gateway_payment_profile_id: confirmSetupData.setupIntent.payment_method,
          name: cardName,
          api_method: "setup_intent_api",
        },
      })
        .then((resp) => {
          const cardObj = Object.values(resp.spreeCreditCards)[0];
          return onSuccess(cardObj);
        })
        .finally(() => setLoading(false))
        .catch((er) => {
          const error = jsonErrorFormatter(er.response, "credit_card");
          const errorMsg = error.credit_card
            ? `${Object.values(error.credit_card)[0]}`
            : GENERIC_ERROR_MESSAGE;
          showError(errorMsg);
          throw er;
        });
  };

  const renderBody = () => (
    <>
      {isCardFormLoading && (
        <div className="payment-spinner-block">
          <Loaders.Spinner />
        </div>
      )}
      <div className={isCardFormLoading ? "d-none" : ""}>
        <div className="form-field">
          <TextInput
            id={cardNameFieldId}
            value={cardName}
            onFocus={clearErrors}
            classes="form-group"
            inputClass={`form-control input-control ${input}`}
            placeholder="Name on Card"
            showAnimatedLine={false}
            onChange={({ value }) => {
              setCardName(value);
            }}
            label={{
              for: cardNameFieldId,
              classes: inputLabel,
              content: "Name on Card",
            }}
          />
          <PaymentElement
            options={paymentElementOptions}
            onReady={() =>
              setTimeout(() => {
                setIsCardFormLoading(false);
              }, 1000)
            }
            onChange={({ complete }) => {
              setIsCardFormComplete(complete);
            }}
            onFocus={clearErrors}
          />
          {showCardNote && (
            <div className={styles.cardNote}>
              Paying for an order outside the US? Make sure to enable international payments on your
              card.
            </div>
          )}
          {extraInfo && <div className={styles.cardNote}>{extraInfo}</div>}
        </div>
      </div>
    </>
  );

  const renderFooter = () => {
    const { text: submitButtonText = "Add", classes: submitButtonClass = "py-0" } =
      submitBtn || emptyObj;
    const isSubmitBtnDisabled =
      isLoading || !stripe || !elements || !cardName?.trim() || !isCardFormComplete;

    return (
      <div className={`d-flex ${styles.cardBtnAction} ${actionButtonsWrapper}`}>
        <ButtonWithLoader
          type="submit"
          isLoading={isLoading}
          className={submitButtonClass}
          disabled={isSubmitBtnDisabled}
          onClick={isSubmitBtnDisabled ? null : handleSubmit}
        >
          {submitButtonText}
        </ButtonWithLoader>
      </div>
    );
  };

  return (
    <form id={id} onSubmit={handleSubmit} className={addCardFormClasses}>
      <div className={bodyWrapper}>{renderBody()}</div>
      {renderCustomFooter ? renderCustomFooter(renderFooter) : renderFooter()}
    </form>
  );
};

const WrappedCardForm = (props) => {
  const { apiClient, stripeKey, elementStyleOptions = DEFAULT_ELEMENT_STYLE_OPTIONS } = props;
  const [clientSecret, setClientSecret] = useState(null);

  useEffect(() => {
    if (!clientSecret) {
      createStripeSetupIntent(apiClient).then((resp) => {
        setClientSecret(resp.client_secret);
      });
    }
  }, []);

  const options = useMemo(() => ({ clientSecret, ...elementStyleOptions }), [clientSecret]);

  if (!clientSecret) {
    return (
      <div className="payment-spinner-block">
        <Loaders.Spinner />
      </div>
    );
  }

  return (
    <StripeWrapper stripeKey={stripeKey} options={options}>
      <AddCardForm {...props} />
    </StripeWrapper>
  );
};

export default WrappedCardForm;
